import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { products } from '../data/mockData';
import { CheckCircle2, Star } from 'lucide-react';

const Products = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState('thermal');

  useEffect(() => {
    const category = searchParams.get('category');
    if (category && products[category]) {
      setActiveCategory(category);
    }
  }, [searchParams]);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setSearchParams({ category });
  };

  const categories = [
    { id: 'thermal', label: t('categories.thermal'), desc: t('categories.thermalDesc') },
    { id: 'gas', label: t('categories.gas'), desc: t('categories.gasDesc') },
    { id: 'climate', label: t('categories.climate'), desc: t('categories.climateDesc') },
    { id: 'electrical', label: t('categories.electrical'), desc: t('categories.electricalDesc') }
  ];

  const currentCategory = categories.find(c => c.id === activeCategory);
  const currentProducts = products[activeCategory] || [];

  return (
    <div className="min-h-screen pt-20 pb-16">
      {/* Header */}
      <section className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('nav.products')}</h1>
            <p className="text-xl text-gray-300">{t('categories.explore')}</p>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Category Tabs */}
          <div className="mb-8 overflow-x-auto">
            <Tabs value={activeCategory} onValueChange={handleCategoryChange} className="w-full">
              <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto">
                {categories.map((cat) => (
                  <TabsTrigger
                    key={cat.id}
                    value={cat.id}
                    className="text-sm md:text-base py-3"
                  >
                    {cat.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Category Description */}
          {currentCategory && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">{currentCategory.label}</h2>
              <p className="text-gray-700">{currentCategory.desc}</p>
            </motion.div>
          )}

          {/* Product Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                  <div className="relative h-64 bg-gray-100 overflow-hidden group">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-500"
                    />
                    <Badge className="absolute top-4 right-4 bg-green-600">
                      <Star className="h-3 w-3 mr-1" />
                      {t('products.quality')}
                    </Badge>
                  </div>
                  <CardHeader>
                    <h3 className="text-xl font-semibold text-gray-900">{product.name}</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{i18n.language === 'en' ? product.descriptionEn : product.descriptionRu}</p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-700">
                        <CheckCircle2 className="h-4 w-4 text-green-600 mr-2" />
                        {t('products.precision')}
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <CheckCircle2 className="h-4 w-4 text-green-600 mr-2" />
                        {t('products.quality')}
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <CheckCircle2 className="h-4 w-4 text-green-600 mr-2" />
                        {t('products.techSupport')}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold text-green-600">{product.price}</span>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700">
                        {t('products.learnMore')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('products.cta.title')}
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              {t('products.cta.subtitle')}
            </p>
            <Button size="lg" className="bg-green-600 hover:bg-green-700">
              {t('products.cta.button')}
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Products;